import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Tooltip } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import SimOpsAPI from 'api/ops/v2/SimOpsAPI'
import { AxiosResponse } from 'axios'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { format, fromUnixTime, parseISO } from 'date-fns'
import OrbitSession from 'hooks/useCookies'
import {
  GetAllFaberUsersDetails,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType,
  GetAllOtpackUserPlansType
} from 'types/api/faber/oneTimePack.type'
import { PackAllocation } from 'types/api/ops/v2/simOps.type'

import {
  CellText,
  fitTableHeight,
  formatDataInGB,
  formatValidityDays,
  listGridProps,
  ListSection,
  MonoText,
  MutedText,
  StatusPill
} from './listUi'

interface TerminatedCustomerProps {
  isExternal: boolean
  userId?: string
}

type TerminatedPlansResponse =
  | AxiosResponse<GetAllOtpackUserPlansType>
  | AxiosResponse<{ status: string; body: { data: PackAllocation[] } }>

const formatDateTime = (value?: string | number) => {
  if (!value) return '—'

  if (typeof value === 'number') {
    return format(fromUnixTime(value / 1000), 'dd MMM yyyy, HH:mm')
  }

  return format(parseISO(value), 'dd MMM yyyy, HH:mm')
}

export default function TerminatedCustomerPlans({
  isExternal,
  userId
}: TerminatedCustomerProps) {
  const location = useLocation()
  const customerInfo = location.state as GetAllFaberUsersDetails

  if (customerInfo?._id === undefined) {
    customerInfo._id = customerInfo.user_id
  }

  const resolvedUserId = userId || customerInfo?._id
  const faberToken = OrbitSession.get('faberToken')

  const terminatedPlansColumns = [
    {
      field: 'packageName',
      headerName: 'Pack',
      sortable: false,
      minWidth: 220,
      flex: 1.2,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => (
        <Tooltip title={`Applied plan ID: ${params.row.appliedPlanId}`}>
          <span>
            <CellText strong>{params.row.packageName}</CellText>
          </span>
        </Tooltip>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Purchased',
      sortable: false,
      minWidth: 160,
      flex: 0.9,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => <MutedText>{formatDateTime(params.row.createdAt)}</MutedText>
    },
    {
      field: 'packageDataAllowed',
      headerName: 'Data',
      sortable: false,
      minWidth: 130,
      flex: 0.7,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => <CellText>{params.row.packageDataAllowed} GB</CellText>
    },
    {
      field: 'planStatus',
      headerName: 'Status',
      sortable: false,
      minWidth: 130,
      flex: 0.7,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => (
        <StatusPill status={params.row.planStatus} />
      )
    },
    {
      field: 'updatedAt',
      headerName: 'Terminated',
      sortable: false,
      minWidth: 170,
      flex: 1,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => <MutedText>{formatDateTime(params.row.updatedAt)}</MutedText>
    }
  ]

  const terminatedAllocationColumns = [
    {
      field: 'catalog_name',
      headerName: 'Pack',
      sortable: false,
      minWidth: 220,
      flex: 1.2,
      renderCell: (params: { row: PackAllocation }) => (
        <Tooltip title={`Pack assignment ID: ${params.row.id}`}>
          <span>
            <CellText strong>
              {params.row.catalogName || params.row.catalog_name}
            </CellText>
          </span>
        </Tooltip>
      )
    },
    {
      field: 'dataInGB',
      headerName: 'Data',
      sortable: false,
      minWidth: 100,
      flex: 0.6,
      renderCell: (params: { row: PackAllocation }) => (
        <CellText>{formatDataInGB(params.row.dataInGB)}</CellText>
      )
    },
    {
      field: 'validityInDays',
      headerName: 'Validity',
      sortable: false,
      minWidth: 100,
      flex: 0.6,
      renderCell: (params: { row: PackAllocation }) => (
        <CellText>{formatValidityDays(params.row.validityInDays)}</CellText>
      )
    },
    {
      field: 'created_at',
      headerName: 'Purchased',
      sortable: false,
      minWidth: 160,
      flex: 0.9,
      renderCell: (params: { row: PackAllocation }) => (
        <MutedText>{formatDateTime(params.row.created_at)}</MutedText>
      )
    },
    {
      field: 'iccid',
      headerName: 'ICCID',
      sortable: false,
      minWidth: 180,
      flex: 1,
      renderCell: (params: { row: PackAllocation }) => (
        <MonoText>{params.row.iccid}</MonoText>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      minWidth: 130,
      flex: 0.7,
      renderCell: (params: { row: PackAllocation }) => (
        <StatusPill status={params.row.status} />
      )
    },
    {
      field: 'updated_at',
      headerName: 'Terminated',
      sortable: false,
      minWidth: 170,
      flex: 1,
      renderCell: (params: { row: PackAllocation }) => (
        <MutedText>{formatDateTime(params.row.updated_at)}</MutedText>
      )
    }
  ]

  const [allTerminatedUserPlans, setAllTerminatedUserPlans] = useState<
    GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  >([])
  const [terminatedAllocations, setTerminatedAllocations] = useState<
    PackAllocation[]
  >([])

  const {
    isFetching: isFetchingTerminatedOtpackUserPlans,
    refetch: refetchTerminatedOtpackUserPlans
  } = useQuery<TerminatedPlansResponse>(
    ['getAllTerminatedOtpackUserPlans', resolvedUserId, isExternal],
    async () => {
      if (isExternal) {
        return OneTimePackApi.getExternalAllTerminatedOtpackUserPlans(
          resolvedUserId,
          faberToken
        )
      }

      return SimOpsAPI.getUserAllocations({
        userId: resolvedUserId,
        limit: 1000,
        offset: 0
      })
    },
    {
      onSuccess: (res) => {
        if (isExternal) {
          const externalRows = (res as AxiosResponse<GetAllOtpackUserPlansType>)
            .data.body.data
          setAllTerminatedUserPlans(externalRows)
          return
        }

        const internalRows = (
          res as AxiosResponse<{ status: string; body: { data: PackAllocation[] } }>
        ).data.body.data

        setTerminatedAllocations(
          (internalRows || []).filter((pack) => pack.status === 'TERMINATED')
        )
      },
      onError: () => {
        return []
      }
    }
  )

  useEffect(() => {
    refetchTerminatedOtpackUserPlans()
  }, [location])

  const rows = isExternal
    ? allTerminatedUserPlans || []
    : terminatedAllocations || []

  return (
    <ListSection
      title='Terminated Packs'
      description="Packs that have been used up or terminated."
      count={rows.length}
    >
      <MUIDataGrid
        {...listGridProps}
        loading={isFetchingTerminatedOtpackUserPlans}
        columns={isExternal ? terminatedPlansColumns : terminatedAllocationColumns}
        rows={rows}
        getRowId={(row) =>
          row.userPlanId || row.appliedPlanId || row.id || row.vendorPlanId
        }
        disableColumnSelector
        hideFooter
        tableHeight={fitTableHeight(rows.length)}
      />
    </ListSection>
  )
}
